import React, { useState, useEffect } from 'react';
import { FileText, GraduationCap, TrendingUp, Award } from 'lucide-react';
import PreadmisionNotas from './PreadmisionNotas';
import ProcesoNotas from './ProcesoNotas';
import PeriodoNotas from './PeriodoNotas';
import ConsolidadoNotas from './ConsolidadoNotas';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function HistorialAcademico() {
    const [activeTab, setActiveTab] = useState('preadmision');
    const [asignaturas, setAsignaturas] = useState([]);
    const [paralelos, setParalelos] = useState([]);
    const [carreras, setCarreras] = useState([]);

    const [filters, setFilters] = useState({
        id_asignatura: '',
        id_paralelo: '',
        id_carrera: '', // NEW: career filter
        gestion: new Date().getFullYear(),
        periodo: 1
    });

    // Cargar asignaturas y carreras del docente
    useEffect(() => {
        loadAsignaturas();
    }, []);

    // Cargar paralelos cuando cambia asignatura
    useEffect(() => {
        if (filters.id_asignatura) {
            loadParalelos(filters.id_asignatura);
        } else {
            setParalelos([]);
        }
    }, [filters.id_asignatura]);

    const loadAsignaturas = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/asignaturas?tipo=matriculacion`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            const asigs = data.asignaturas || [];
            setAsignaturas(asigs);

            // Extract unique careers from asignaturas
            const uniqueCarreras = [];
            const seen = new Set();
            asigs.forEach(a => {
                if (a.carrera && !seen.has(a.carrera)) {
                    seen.add(a.carrera);
                    uniqueCarreras.push(a.carrera);
                }
            });
            setCarreras(uniqueCarreras.sort());
        } catch (error) {
            console.error('Error loading asignaturas:', error);
        }
    };

    const loadParalelos = async (id_asignatura) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/paralelos/${id_asignatura}?tipo=matriculacion`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setParalelos(data.paralelos || []);
        } catch (error) {
            console.error('Error loading paralelos:', error);
        }
    };

    // Filter asignaturas by selected career
    const filteredAsignaturas = filters.id_carrera
        ? asignaturas.filter(a => a.carrera === filters.id_carrera)
        : asignaturas;

    const tabs = [
        { id: 'preadmision', label: 'Prueba de Admisión Académica', icon: GraduationCap },
        { id: 'proceso', label: 'Proceso', icon: TrendingUp },
        { id: 'periodo', label: 'Final Periodo', icon: FileText },
        { id: 'consolidado', label: 'Acta de Calificaciones', icon: Award }
    ];

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4">
                    <h2 className="text-2xl font-bold mb-1">Evaluaciones</h2>
                    <p className="text-sm text-gray-500 mb-4">Docente &gt; Evaluaciones</p>

                    {/* Filtros Globales */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        {/* Carrera */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Carrera</label>
                            <select
                                value={filters.id_carrera}
                                onChange={(e) => setFilters({ ...filters, id_carrera: e.target.value, id_asignatura: '', id_paralelo: '' })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Todas las Carreras</option>
                                {carreras.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>

                        {/* Asignatura */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Asignatura</label>
                            <select
                                value={filters.id_asignatura}
                                onChange={(e) => setFilters({ ...filters, id_asignatura: e.target.value, id_paralelo: '' })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Seleccionar Asignatura</option>
                                {filteredAsignaturas.map(a => (
                                    <option key={`${a.id_asignatura}-${a.carrera}`} value={a.id_asignatura}>
                                        {a.nombre} ({a.codigo})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Paralelo */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Paralelo</label>
                            <select
                                value={filters.id_paralelo}
                                onChange={(e) => setFilters({ ...filters, id_paralelo: e.target.value })}
                                disabled={!filters.id_asignatura}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                            >
                                <option value="">Todos los Paralelos</option>
                                {paralelos.map(p => (
                                    <option key={p.id_paralelo} value={p.id_paralelo}>
                                        Paralelo {p.nombre}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Gestión */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Gestión</label>
                            <select
                                value={filters.gestion}
                                onChange={(e) => setFilters({ ...filters, gestion: parseInt(e.target.value) })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {Array.from({ length: 21 }, (_, i) => 2020 + i).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>

                        {/* Periodo */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Periodo</label>
                            <select
                                value={filters.periodo}
                                onChange={(e) => setFilters({ ...filters, periodo: parseInt(e.target.value) })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value={1}>Periodo 1</option>
                                <option value={2}>Periodo 2</option>
                                <option value={3}>Periodo 3</option>
                                <option value={4}>Periodo 4</option>
                            </select>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="mt-6 border-b border-gray-200">
                        <div className="flex space-x-4">
                            {tabs.map(tab => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`
                      flex items-center gap-2 px-4 py-2 border-b-2 transition-colors
                      ${activeTab === tab.id
                                                ? 'border-blue-600 text-blue-600 font-semibold'
                                                : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                                            }
                    `}
                                    >
                                        <Icon size={18} />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
                    {activeTab === 'preadmision' && <PreadmisionNotas filters={filters} />}
                    {activeTab === 'proceso' && <ProcesoNotas filters={filters} />}
                    {activeTab === 'periodo' && <PeriodoNotas filters={filters} />}
                    {activeTab === 'consolidado' && <ConsolidadoNotas filters={filters} />}
                </div>
            </div>
        </div>
    );
}
